import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
} from "@mui/material";
import { useSessionClass } from "contexts/SessionClassContext";
import { useMarkSheets } from "hooks/useMarkSheets";
import { useAuth } from "hooks/useAuth";
import { useSchools } from "hooks/useSchools";
import { MARKSHEET_TYPE_OPTIONS } from "components/enum/MarksheetTypeOptions";
import { useTranslation } from "react-i18next";
import MeritListTemplate from "components/templates/MeritListTemplate";
import { ListAlt } from "@mui/icons-material";

const MeritListManagement: React.FC = () => {
  const { t } = useTranslation();
  const { selectedClass, selectedSession } = useSessionClass();
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const { useSchoolById } = useSchools();
  const [marksheetType, setMarksheetType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState("");

  // Get current school data
  const schoolId = user?.schoolId ?? undefined;
  const { data: currentSchool } = useSchoolById(schoolId);

  // Set default marksheet type based on class
  React.useEffect(() => {
    if (selectedClass && typeof selectedClass.name === "string") {
      const className = selectedClass.name.trim();
      if (className.startsWith("10th") || className.startsWith("12th")) {
        setMarksheetType("BOARD");
      } else {
        setMarksheetType("ANNUAL");
      }
    } else {
      setMarksheetType(""); // or "ANNUAL" as default if you prefer
    }
  }, [selectedClass]);

  // Prepare the payload for meritlist
  const meritlistPayload = useMemo(
    () => {
      const rawClassName = String(selectedClass?.name ?? "").trim();
      const isAllClass =
        (selectedClass?.id != null && Number(selectedClass.id) < 0) ||
        /\bALL\b$/i.test(rawClassName.replace(/-/g, " "));

      const classNameForApi = isAllClass
        ? (() => {
            if (/-ALL$/i.test(rawClassName)) return rawClassName.replace(/-all$/i, "-ALL");
            if (/\s+ALL$/i.test(rawClassName)) return rawClassName.replace(/\s+ALL$/i, "-ALL");
            return rawClassName ? `${rawClassName}-ALL` : "ALL";
          })()
        : rawClassName;

      return {
        schoolId: user?.schoolId ?? 0,
        classId: isAllClass ? null : (selectedClass?.id ?? 0),
        className: classNameForApi,
        sessionId: selectedSession?.id ?? 0,
        marksheetType,
      };
    },
    [user, selectedClass, selectedSession, marksheetType]
  );

  // Fetch meritlist - disable marksheetsQuery for students
  const { useMeritlist } = useMarkSheets(false, isStudent);
  const {
    data: meritList,
    isLoading,
    error,
  } = useMeritlist(
    meritlistPayload,
    !!user?.schoolId && !!selectedClass && !!selectedSession && !!marksheetType
  );

  const className = selectedClass?.name?.toLowerCase() || "";
  const isBoardClass =
    className.startsWith("10th") || className.startsWith("12th");

  const filteredOptions = MARKSHEET_TYPE_OPTIONS.filter((opt) => {
    if (isBoardClass) {
      // Show BOARD, PRE_BOARD, QUARTERLY, HALF_YEARLY
      return ["BOARD", "PRE_BOARD", "QUARTERLY", "HALF_YEARLY"].includes(
        opt.value
      );
    } else {
      // Show ANNUAL, QUARTERLY, HALF_YEARLY
      return ["ANNUAL", "QUARTERLY", "HALF_YEARLY"].includes(opt.value);
    }
  });

  const handleOpenDialog = () => {
    setSelectedExamType(marksheetType);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };


  // Sort merit list by percentage in descending order
  const sortedMeritList = useMemo(() => {
    if (!meritList || meritList.length === 0) return [];
    
    const sorted = [...meritList].sort((a, b) => {
      // Use any property that might contain percentage data
      const percentageA = (a as any).percentage || (a as any).totalMarks || 0;
      const percentageB = (b as any).percentage || (b as any).totalMarks || 0;
      return percentageB - percentageA; // Descending order (highest first)
    });
    
    return sorted;
  }, [meritList]);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 2 }}>
      <Card sx={{ 
        p: 4, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <ListAlt sx={{ fontSize: 28, color: '#64b5f6', mr: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
            {selectedClass?.name} {t("merit_list")}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#666', fontWeight: 500 }}>
              {t("exam_type")}  
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={marksheetType}
                onChange={(e) => setMarksheetType(e.target.value)}
                sx={{
                  height: 40,
                  borderRadius: 1,
                  backgroundColor: '#ffffff',
                  border: '1px solid #ddd',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused': {
                    borderColor: 'primary.main',
                  }
                }}
              >
                {filteredOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ListAlt />}
            onClick={handleOpenDialog}
            disabled={
              !user?.schoolId ||
              !selectedClass ||
              !selectedSession ||
              !marksheetType
            }
          >
            {t("get_merit_list")}
          </Button>
        </Box>
      </Card>

      {(!user?.schoolId ||
      !selectedClass ||
      !selectedSession ||
      !marksheetType) && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            {t(
              "please_select_a_class_session_and_marksheet_type_to_view_the_merit_list"
            )}
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      )}

      {/* Dialog for Merit List Template */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle>
          {selectedClass?.name} {t("merit_list")} - {selectedExamType}
        </DialogTitle>
        <DialogContent>
          {sortedMeritList && sortedMeritList.length > 0 ? (
            <MeritListTemplate
              meritListData={sortedMeritList}
              schoolName={currentSchool?.nameofSchool || "School Name"}
              district={currentSchool?.district || "District"}
              block={currentSchool?.blockName || "Block"}
              diseCode={currentSchool?.diseCode || "DISE Code"}
              mpbseCode={currentSchool?.schoolCode || "School Code"}
              className={selectedClass?.name || ""}
              examType={selectedExamType}
              sessionYear={selectedSession?.name || ""}
              hidePrintButton={false}
            />
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                No merit list data available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please check if there are students with marks for the selected
                exam type.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 6 }}>
          <Button onClick={handleCloseDialog}>{t("close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeritListManagement;
